/**
 * A variant's colour and pattern, put on the axes its family shows (ARDE-293).
 *
 * A family names its colour axis after when it was made — 'label' (surfaces),
 * 'color', or 'color-f' (furniture) — but products added by AI (the demo
 * generator, its MCP, the scraper) were written with `axes.color`, and their
 * pattern only on the texture or in a spelling the pattern grid does not know.
 * The editor reads only the family's own axis names, so the colour and the
 * pattern were on the product and nowhere on screen.
 *
 * The API now saves them where they belong; this reads products saved before
 * that, and seeds a new product from the scraper the same way.
 */

import type { VariantAxisConfig } from '../../../../common/models/product-family.model';
import { knownPatternName } from '../../../../common/utils/pattern-name';
import type { AxisValue, ColorAxisValue } from '../types';

const COLOR_NAME = /^colou?r(?:$|[-_])/i;
/** Any axis that holds a variant's colour: label, color, colour, color-f, color_xyz… */
export const isColorAxisName = (name: string): boolean => name === 'label' || COLOR_NAME.test(name);

/** The name this family gives its colour axis ('label', 'color', 'color-f'…), if it has one. */
export function colorAxisNameOf(axes: Pick<VariantAxisConfig, 'name'>[] | null | undefined): string | undefined {
  return (axes || []).find((a) => a?.name && isColorAxisName(a.name))?.name;
}

/** A colour as text: a string, or the `value` of a `{ value, hex }` object. */
function colorText(value: unknown): string | undefined {
  if (typeof value === 'string') return value.trim() || undefined;
  if (value && typeof value === 'object' && typeof (value as any).value === 'string') {
    return (value as any).value.trim() || undefined;
  }
  return undefined;
}

/**
 * A colour as the axis `axisName` holds it: 'color' and 'label' keep
 * `{ value, hex }` (what the editor saves for them), any other colour axis is text.
 */
export function colorValueForAxis(axisName: string, raw: unknown): AxisValue | undefined {
  const text = colorText(raw);
  if (!text) return undefined;
  if (axisName !== 'color' && axisName !== 'label') return text;
  const hex = (raw as any)?.hex;
  return { value: text, hex: typeof hex === 'string' ? hex : '' } as ColorAxisValue;
}

/**
 * A stored variant's axes as the editor shows them (a new object):
 * - the colour on the family's colour axis, moved there from whichever colour
 *   key it was saved under (a key the family does not show is dropped, so it
 *   cannot make the variant look different from its own combination);
 * - the pattern in the pattern grid's name ('Brick bond' → 'bricks'), taken
 *   from the texture when the axis is empty. A name the generator does not
 *   know is left as it was saved.
 */
export function alignLoadedAxes(
  axes: Record<string, AxisValue> | null | undefined,
  enabledAxes: Pick<VariantAxisConfig, 'name'>[],
  visualization?: { type?: string; surface?: { seamlessParams?: { pattern?: string } } } | null,
): Record<string, AxisValue> {
  const out: Record<string, AxisValue> = { ...(axes || {}) };
  const shown = new Set(enabledAxes.map((a) => a.name));

  const colorAxis = colorAxisNameOf(enabledAxes);
  if (colorAxis) {
    const strays = Object.keys(out).filter((k) => k !== colorAxis && !shown.has(k) && isColorAxisName(k));
    if (!colorText(out[colorAxis])) {
      const source = ['color', 'label', ...strays].find((k) => strays.includes(k) && colorText(out[k]));
      const value = source ? colorValueForAxis(colorAxis, out[source]) : undefined;
      if (value !== undefined) out[colorAxis] = value;
    }
    strays.forEach((k) => delete out[k]);
  }

  if (shown.has('pattern')) {
    const pattern = out.pattern;
    if (typeof pattern === 'string' && pattern.trim()) {
      const known = knownPatternName(pattern);
      if (known) out.pattern = known;
    } else if (visualization?.type === 'surface') {
      const fromTexture = knownPatternName(visualization.surface?.seamlessParams?.pattern);
      if (fromTexture) out.pattern = fromTexture;
    }
  }

  return out;
}

